import { Fragment, createElement, type ReactNode } from "react";

// Lantern is written in English, and each string is looked up by its
// English in the reader's language's catalog, as gettext does; a string a
// catalog lacks stays English. The language is chosen once, before the app
// starts: changing it reloads the page.
//
// Strings go through t() (or tc, tn, tx) as literals, with {name} for what is filled in, so
// i18n.test.ts can find them all and check every catalog has each.

export type Language = "en" | "zh-CN";
export type Catalog = Record<string, string>;

// languages are those one can choose, each named in itself.
export const languages: { id: Language; name: string }[] = [
  { id: "en", name: "English" },
  { id: "zh-CN", name: "简体中文" },
];

const storageKey = "lantern.language";
// pickedKey marks a language picked before signing in, to keep as the
// person's preference once they have.
const pickedKey = "lantern.language.picked";

// language is the one the app runs in, and locale the same for Intl's
// dates and numbers.
export let language: Language = "en";
export let locale = "en";
let catalog: Catalog = {};

// choose picks the language: the one chosen, if any, or else the first of
// the browser's that Lantern is written in.
export function choose(chosen: string | null, browser: readonly string[]): Language {
  if (chosen && languages.some((l) => l.id === chosen)) return chosen as Language;
  for (const raw of browser) {
    const l = raw.toLowerCase();
    if (l === "en" || l.startsWith("en-")) return "en";
    // Chinese is written in simplified characters only, so far.
    if (l === "zh" || l.startsWith("zh-")) return "zh-CN";
  }
  return "en";
}

// chosen is the language set for this browser, from the person's
// preference, or "" to follow the browser.
export function chosen(): string {
  try {
    return localStorage.getItem(storageKey) ?? "";
  } catch {
    return "";
  }
}

// setChosen keeps the language set, and reports whether the app must reload
// to show it. Where it cannot be kept, the app stays as it is, rather than
// reload into the same language again.
export function setChosen(lang: string): boolean {
  try {
    if (lang) localStorage.setItem(storageKey, lang);
    else localStorage.removeItem(storageKey);
  } catch {
    return false;
  }
  return choose(lang, navigator.languages ?? [navigator.language]) !== language;
}

// pick sets the language before one has signed in, and reloads into it;
// takePicked hands it to the session to keep once signed in.
export function pick(lang: string) {
  try {
    localStorage.setItem(pickedKey, "1");
  } catch {
    return;
  }
  if (setChosen(lang)) location.reload();
}

export function takePicked(): string | null {
  try {
    if (!localStorage.getItem(pickedKey)) return null;
    localStorage.removeItem(pickedKey);
  } catch {
    return null;
  }
  return chosen();
}

// startLanguage picks the language and loads its catalog, before the app
// first renders.
export async function startLanguage() {
  use(choose(chosen(), typeof navigator === "undefined" ? [] : (navigator.languages ?? [navigator.language])));
  if (language === "zh-CN") catalog = (await import("./locales/zh-CN")).default;
  if (typeof document !== "undefined") document.documentElement.lang = language;
}

// use sets the language and catalog; tests use it too.
export function use(lang: Language, c: Catalog = {}) {
  language = lang;
  locale = lang;
  catalog = c;
}

function fill(s: string, vars?: Record<string, string | number>): string {
  return vars ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : s;
}

// t is a string in the reader's language, with {name} filled in from vars.
export function t(en: string, vars?: Record<string, string | number>): string {
  return fill(catalog[en] ?? en, vars);
}

// tc is t for English that means different things in different places,
// told apart by a context the catalogs key it under, as "context|English":
// tc("date", "From") is not the "From" of a message's sender.
export function tc(context: string, en: string, vars?: Record<string, string | number>): string {
  return fill(catalog[`${context}|${en}`] ?? en, vars);
}

// tn is t for a count, which English says in one way for one and another
// for the rest: tn(n, "{n} reply", "{n} replies"). Catalogs hold the second.
export function tn(n: number, one: string, other: string, vars?: Record<string, string | number>): string {
  return fill(catalog[other] ?? (n === 1 ? one : other), { n, ...vars });
}

// tx is t with parts that are elements, such as a link in a sentence:
// tx("{name} marked as read", { name: <a>…</a> }).
export function tx(en: string, parts: Record<string, ReactNode>): ReactNode {
  const s = catalog[en] ?? en;
  const out: ReactNode[] = [];
  let at = 0;
  for (const m of s.matchAll(/\{(\w+)\}/g)) {
    if (!(m[1] in parts)) continue;
    out.push(s.slice(at, m.index), createElement(Fragment, { key: out.length }, parts[m[1]]));
    at = m.index + m[0].length;
  }
  out.push(s.slice(at));
  return createElement(Fragment, null, ...out);
}

// list joins words as the language does: "a, b and c", or "a、b和c".
export function list(items: string[], type: "conjunction" | "disjunction" = "conjunction"): string {
  return new Intl.ListFormat(locale, { style: "long", type }).format(items);
}
